import React, { FC, useEffect, useState } from 'react'
import { Select, Alert } from 'antd'
import { RuleConditionFields, JsonLogicRule } from './types'
import './styles.modules.scss'
import { supportedOperators, findFieldByValue } from './utils'
import { extractRuleComponents, createRuleLogic } from './ruleLogicUtils'

interface RuleLogicEditorProps {
  ruleLogic: JsonLogicRule
  supportedRuleConditionFields: RuleConditionFields[]
  editMode: boolean
  onRuleChange: (updatedRuleLogic: JsonLogicRule, validationError?: string) => void
  setRuleName: (ruleName: string) => void
  setDefaultIneligibilityReason: (defaultIneligibilityReason: string) => void
}

interface RuleComponents {
  field: string
  operator: string
  value: string | string[]
  displayValue: string
  validationError?: string
}

export const RuleLogicEditor: FC<RuleLogicEditorProps> = ({
  ruleLogic,
  supportedRuleConditionFields,
  editMode,
  onRuleChange,
  setRuleName,
  setDefaultIneligibilityReason
}) => {
  // Helper function to convert value to display string
  const getDisplayValue = (value: string | string[]): string => {
    if (Array.isArray(value)) {
      return value.join(', ')
    }
    return String(value)
  }

  // Initialize state with extracted components
  const [components, setComponents] = useState<RuleComponents>(() => {
    const { field, operator, value } = extractRuleComponents(ruleLogic)
    return {
      field,
      operator,
      value,
      displayValue: getDisplayValue(value)
    }
  })

  // Function to validate the input value based on operator
  const validateValue = (value: string, operator: string): string | undefined => {
    // Check if the operator allows multiple values
    const operatorConfig = supportedOperators.find((op) => op.value === operator)
    if (!operatorConfig?.allowMultipleValues && value.includes(',')) {
      return 'Multiple values are not allowed for this operator. Use an operator that supports multiple values.'
    }

    // Don't allow special characters except commas for "in" and "none" operators
    const specialCharsRegex = /[!@#$%^&*()_+\-=\[\]{};':"\\|.<>\/?]+/
    if (specialCharsRegex.test(value)) {
      return 'Special characters are not allowed.'
    }

    // Only allow strings or numbers
    const validInputRegex = /^[a-zA-Z0-9,\s]*$/
    if (!validInputRegex.test(value)) {
      return 'Only alphanumeric characters are allowed.'
    }

    return undefined
  }

  // Convert empty strings to "Empty" for display only if operator allows empty values
  const convertForDisplay = (values: string | string[]): string[] => {
    validateValue(String(values), components.operator)
    const operatorConfig = supportedOperators.find((op) => op.value === components.operator)

    if (Array.isArray(values)) {
      return values.map((v) => (v === '' && operatorConfig?.showEmpty ? 'Empty' : v))
    } else if (values === '') {
      return operatorConfig?.showEmpty ? ['Empty'] : ['']
    } else if (typeof values === 'string') {
      return [values]
    }
    return []
  }

  // State to track selected values
  const [selectedValues, setSelectedValues] = useState<string[]>(() => {
    return convertForDisplay(components.value)
  })

  // Update state when ruleLogic changes
  useEffect(() => {
    const { field, operator, value } = extractRuleComponents(ruleLogic)
    const displayValue = getDisplayValue(value)
    const validationError = validateValue(displayValue, operator)

    setComponents({
      field,
      operator,
      value,
      displayValue,
      validationError
    })

    setSelectedValues(convertForDisplay(value))
  }, [ruleLogic])

  // Convert "Empty" to empty string for storage
  const convertForStorage = (values: string[], operator: string): string | string[] => {
    const operatorConfig = supportedOperators.find((op) => op.value === operator)

    if (!operatorConfig?.allowMultipleValues) {
      const value = values[0] === 'Empty' ? '' : values[0]
      return value
    }

    return values.map((v) => (v === 'Empty' ? '' : v))
  }

  // Handlers for field, operator, and value changes
  const handleFieldChange = (newField: string) => {
    const validationError = validateValue(String(selectedValues), components.operator)
    setComponents({
      ...components,
      field: newField,
      validationError
    })

    const valueForStorage = convertForStorage(selectedValues, components.operator)
    const updatedRuleLogic = createRuleLogic(newField, components.operator, valueForStorage)

    onRuleChange(updatedRuleLogic)
    setRuleName(findFieldByValue(newField, supportedRuleConditionFields)?.name || '')
    setDefaultIneligibilityReason(getInEligibilityReason(newField, components.operator, [...selectedValues]))
  }

  const handleOperatorChange = (newOperator: string) => {
    // Clear values when operator changes
    const newValues: string[] = []
    setSelectedValues(newValues)

    setComponents({
      ...components,
      operator: newOperator,
      value: '',
      displayValue: '',
      validationError: undefined
    })

    const updatedRuleLogic = createRuleLogic(components.field, newOperator, '')
    onRuleChange(updatedRuleLogic)
    setDefaultIneligibilityReason(getInEligibilityReason(components.field, newOperator, newValues))
  }

  const handleValueChange = (newValues: string[]) => {
    setSelectedValues(newValues)
    const validationError = validateValue(String(newValues), components.operator)
    const valueForStorage = convertForStorage(newValues, components.operator)
    const updatedRuleLogic = createRuleLogic(components.field, components.operator, valueForStorage)

    onRuleChange(updatedRuleLogic, validationError)

    setDefaultIneligibilityReason(getInEligibilityReason(components.field, components.operator, [...newValues]))
  }

  // Helper function to get the field name from its value
  const getFieldName = (fieldValue: string): string => {
    const field = findFieldByValue(fieldValue, supportedRuleConditionFields)
    return field ? field.name : fieldValue
  }

  const getInEligibilityReason = (field: string, operator: string, selectedValues: string[]): string => {
    const fieldName = getFieldName(field)
    const formattedValues = selectedValues.map((v) => (v === 'Empty' ? '(empty)' : v)).join(', ')
    return getRuleInSentenceFormat(operator, fieldName, formattedValues)
  }

  // Helper function to format the rule as a sentence
  const formatRuleAsSentence = (selectedValues: string[]): string => {
    const fieldName = getFieldName(components.field)

    // Format the display values, replacing "Empty" with "(empty)"
    const formattedValues = selectedValues.map((v) => (v === 'Empty' ? '(empty)' : v)).join(', ')

    // Format based on operator
    return getRuleInSentenceFormat(components.operator, fieldName, formattedValues)
  }

  const getRuleInSentenceFormat = (operator: string, fieldName: string, formattedValues: string): string => {
    switch (operator) {
      case '==':
      case 'in':
        return `${fieldName} must be ${formattedValues}`
      case '!=':
      case 'not_in':
        return `${fieldName} must not be ${formattedValues}`
      case 'includes_substring':
        return `${fieldName} must contain "${formattedValues}"`
      case 'excludes_substring':
        return `${fieldName} must not contain "${formattedValues}"`
      case '>':
        return `${fieldName} must be greater than ${formattedValues}`
      case '<':
        return `${fieldName} must be less than ${formattedValues}`
      default:
        return `${fieldName} ${components.operator} ${formattedValues}`
    }
  }

  return (
    <>
      {components.validationError && (
        <Alert description={components.validationError} type="error" showIcon style={{ marginBottom: '16px' }} />
      )}
      <div className="ruleLogicFieldsWrapper">
        {editMode ? (
          <>
            <Select
              placeholder="Select field"
              value={components.field}
              onChange={handleFieldChange}
              className="ruleField"
              disabled={!editMode}
            >
              {supportedRuleConditionFields.map((fieldOption) => (
                <Select.Option key={fieldOption.value} value={fieldOption.value}>
                  {fieldOption.name}
                </Select.Option>
              ))}
            </Select>

            <Select
              value={components.operator}
              onChange={handleOperatorChange}
              className="ruleField"
              disabled={!editMode}
            >
              {supportedOperators.map((op) => (
                <Select.Option key={op.value} value={op.value}>
                  {op.label}
                </Select.Option>
              ))}
            </Select>

            {supportedOperators.find((op) => op.value === components.operator)?.showEmpty ? (
              <Select
                placeholder="Enter value"
                value={selectedValues}
                onChange={handleValueChange}
                className="ruleField"
                disabled={!editMode}
                mode="tags"
                tokenSeparators={[',']}
              >
                <Select.Option key="empty" value="Empty">
                  Empty
                </Select.Option>
              </Select>
            ) : (
              <input
                type="text"
                placeholder="Enter value"
                value={selectedValues[0] || ''}
                onChange={(e) => handleValueChange([e.target.value])}
                className="ruleField"
                disabled={!editMode}
              />
            )}
          </>
        ) : (
          // View mode - show Rule as sentence
          <div className="ruleLogicText">{formatRuleAsSentence(selectedValues)}</div>
        )}
      </div>
    </>
  )
}
